import {
  Avatar,
  Box,
  Button,
  ButtonSize,
  Combobox,
  FormControl,
  Icon,
  IconSize,
  Text,
  TextFieldSize,
  Theme,
} from '@bechtel/gds';
import {
  CommodityStatusCode,
  CrewMember,
  CrewMemberDiscipline,
  CrewMemberList,
  PagingInput,
  PersonaType,
  SearchCrewMemberInput,
  Weld,
} from '@bechtel/types';
import { NetworkStatus, useLazyQuery } from '@apollo/client';
import { find, get } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { ThemeContext } from 'styled-components';
import { GET_CREW_MEMBERS } from '@bechtel/client-gql';
import { useName, useDebounce, ProjectAppTypes } from '@bechtel/client-common';
import { CrewIconStyled } from './styled';
import { t, Trans } from '@lingui/macro';

export const UserOption = ({ item }: any) => {
  const { getFullName } = useName();
  const userName = !item?.firstName ? t`Unassigned` : getFullName(item.firstName, item.lastName);
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        {item?.imageUrl ? (
          <Avatar data-testid="avatar-regular-owner" src={item.imageUrl} />
        ) : (
          <CrewIconStyled>
            <Icon fillColor="white" size={IconSize.LARGE} icon="user-plus" />
          </CrewIconStyled>
        )}
      </Box>
      <Text size="400" family="graphik" color="grey.1000">
        {userName}
      </Text>
    </Box>
  );
};

export interface WeldUserItemProps {
  weld: Weld;
  onSelect: (user: CrewMember) => void;
}

export const WeldUserItem: React.FC<WeldUserItemProps> = ({ weld, onSelect }) => {
  const { getFullName } = useName();
  const { id, commodityAssignees, status } = weld;
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText?.trim(), 500);
  const { fontFamilies } = useContext<Theme>(ThemeContext);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const commodityAssignee = find(commodityAssignees, (item) => {
    return item.personaType == PersonaType.FOREMAN;
  });
  const assignedWeldForeman = commodityAssignee?.assignee ? commodityAssignee?.assignee : null;
  const unAssign: CrewMember = {
    id: '',
    imageUrl: '',
    firstName: t`Unassign`,
    lastName: '',
    persona: PersonaType.FOREMAN,
  };
  const [selection, setSelection] = useState(assignedWeldForeman);

  const [getCrewMembers, { data, error, loading, refetch, networkStatus }] = useLazyQuery<
    { crewMembers: CrewMemberList },
    { input: SearchCrewMemberInput; paging: PagingInput }
  >(GET_CREW_MEMBERS, {
    notifyOnNetworkStatusChange: true,
  });
  useEffect(() => {
    if (debouncedSearchText?.length > 0 && debouncedSearchText?.length < 3) return;
    getCrewMembers({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
          persona: PersonaType.FOREMAN,
          appTypeId: ProjectAppTypes.AS
        },
        paging: {
          cursor: '1',
          limit: 30,
        },
      },
    });
  }, [debouncedSearchText, getCrewMembers]);

  const crewMembersList = get(data, 'crewMembers.items', []) as CrewMemberList['items'];
  const crewMembersListWithUnAssign = crewMembersList.reduce(
    (crewMember, item) => {
      return crewMember.concat(item);
    },
    [unAssign]
  );

  return (
    <>
      {!loading && error && (
        <Box px={{ sm: '200' }} css="height: 100%;" data-testid="retry">
          <Box
            flexGrow={{ sm: 1 }}
            alignItems={{ sm: 'center' }}
            flexDirection={{ sm: 'column' }}
            justifyContent={{ sm: 'center' }}
          >
            <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
              <Text>
                <Trans>There was a problem loading welding foremen.Please click the button below to try again.</Trans>
              </Text>
            </Box>
            <Box>
              <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                <Box mr={{ sm: '400' }}>
                  <Icon icon="undo" />
                </Box>
                <Trans>Retry</Trans>
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {!error && (
        <FormControl
          label={t`Assigned To`}
          css={{ flexGrow: 1, fontFamily: fontFamilies['graphik'] }}
          data-testid={`assigneedropdown-${id}`}
        >
          <Combobox
            ariaLabel="weld-foreman"
            size={TextFieldSize.XLARGE}
            items={assignedWeldForeman ? crewMembersListWithUnAssign : crewMembersList}
            itemComponent={UserOption}
            selectedComponent={UserOption}
            term={searchText}
            setTerm={setSearchText}
            placeholder={t`Search Weld Foreman`}
            itemString={itemToString}
            selectedItem={selection !== null ? selection : t`Unassigned`}
            onSelect={(item: any) => {
              setSelection(item);
              onSelect(item);
            }}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchText(event.target.value);
            }}
            loading={loading || networkStatus === NetworkStatus.refetch}
            restorePreviousOnSkip
            messageOnNoResults={t`No results found`}
            disabled={
              status?.status === (CommodityStatusCode.VT_APPROVED || CommodityStatusCode.CLAIMED) ? true : false
            }
          />
        </FormControl>
      )}
    </>
  );
};
